import { getRouteApi } from "@tanstack/react-router";
import { type SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { type businessUnitUsersQuery } from "../api/queries";
import { userTableColumns } from "./Columns";
import { UserTableFilters } from "./Filters";
import { filterUsers } from "./utils";
import { DataTable } from "@/components/DataTable";
import { useClientPagination } from "@/components/Pagination/useClientPagination";
import { type ResultOf } from "@/graphql";
import { getErrorMessage } from "@/lib/utils/errors";

type BusinessUnitUser = NonNullable<ResultOf<typeof businessUnitUsersQuery>["businessUnitUsers"]>[0];
type User = NonNullable<BusinessUnitUser>;
export type UserStatus = User["status"];

interface UserTableProps {
  users: User[];
  loading?: boolean;
  error?: Error | null;
  onUserUpdated?: () => void;
}

const routeApi = getRouteApi("/(authed)/_layout/settings/users");

export function UserTable({ users, loading = false, error, onUserUpdated }: UserTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "user", desc: false }]);
  const searchParams = routeApi.useSearch();

  // Filter users based on URL params
  const filteredUsers = useMemo(() => filterUsers(users, searchParams), [users, searchParams]);

  const columns = useMemo(() => userTableColumns(onUserUpdated), [onUserUpdated]);
  const pagination = useClientPagination({ totalCount: filteredUsers.length });

  const sortConfig = useMemo(
    () => ({
      sorting,
      onSortingChange: setSorting,
    }),
    [sorting],
  );

  return (
    <div className="space-y-4">
      <div className="px-5">
        <UserTableFilters
          filters={searchParams}
          userCount={users.length}
          filteredCount={filteredUsers.length}
          loading={loading}
          error={error}
        />
      </div>

      <DataTable
        data={filteredUsers}
        columns={columns}
        tableId="usersTable"
        loading={loading}
        errorMessage={error ? getErrorMessage(error) : undefined}
        pagination={pagination}
        sortConfig={sortConfig}
      />
    </div>
  );
}
